import { devtoolsExchange } from '@urql/devtools';
import { authExchange } from '@urql/exchange-auth';
import { minifyIntrospectionQuery } from '@urql/introspection';
import { buildSchema, introspectionFromSchema } from 'graphql';
import type { Data, Cache as GraphCache } from '@urql/exchange-graphcache';
import { cacheExchange } from '@urql/exchange-graphcache';
import { relayPagination } from '@urql/exchange-graphcache/extras';
import type {
    Block,
    BlockCursorConnection,
    BlockEdge,
    CreateEvaluationPointPayload,
    DeleteEvaluationPointPayload,
    MatrixChoiceAnswer,
    MutationAddMachineToEnvironmentTemplateArgs,
    MutationAddUsersToExamEventArgs,
    MutationArchiveExamArgs,
    MutationBeginExamPassArgs,
    MutationCopyPasteAssignmentArgs,
    MutationCreateDiskTemplateArgs,
    MutationCreateEvaluationPointArgs,
    MutationCreateExamArgs,
    MutationCreateExamEventArgs,
    MutationCreateVariantExamArgs,
    MutationDeleteAssignmentArgs,
    MutationDeleteDiskTemplateArgs,
    MutationDeleteEvaluationPointArgs,
    MutationDeleteExamArgs,
    MutationDeleteExamEventArgs,
    MutationDeleteMediaObjectArgs,
    MutationExtendAutoArchiveExamArgs,
    MutationRecordAudioBlockPlaybackArgs,
    MutationRemoveMachineFromEnvironmentTemplateArgs,
    MutationRemoveUsersFromExamEventArgs,
    MutationUpdateAssignmentArgs,
    MutationUpdateBlockArgs,
    MutationUpdateEvaluationPointArgs,
    MutationUpdateExamArgs,
    MutationUpdateTaskBlockArgs,
    MutationUpdateUserArgs,
    MutationUploadMediaObjectArgs,
    RightWrongDontKnowAnswer,
} from '~/types/graphql';
import type { ClientOptions } from 'urql';
import { createClient, fetchExchange } from 'urql';

import { env } from './config';
import { ExamPrimaryInstanceMachinesDocument } from './graphql/ExamPrimaryInstanceMachinesQuery.generated';
import { AssignmentUpdateCacheFragmentDoc } from './urql/resolvers/assignments/graphql/AssignmentUpdateCacheFragment.generated';
import type { ChoicesResolverResult } from './urql/resolvers/choicesResolver';
import { choicesResolver } from './urql/resolvers/choicesResolver';
import { createCorrectionApprovalUpdate } from './urql/resolvers/correctionApproval/createCorrectionApprovalUpdate';
import { deleteCorrectionApprovalUpdate } from './urql/resolvers/correctionApproval/deleteCorrectionApprovalUpdate';
import { finishExamPassUpdate } from './urql/resolvers/examPass/finishExamPassUpdate';
import { uncapitalize } from './util/strings';

import type { AssignmentDataFragment } from '~/components/Blocks/AssignmentData.generated';
import { AssignmentDataFragmentDoc } from '~/components/Blocks/AssignmentData.generated';
import type { ReorderBlockMutation } from '~/components/Blocks/BlockMutationReorder.generated';
import type { CopyPasteAssignmentMutation } from '~/components/Blocks/CopyPasteAssignmentMutation.generated';
import type { RecordAudioBlockPlaybackMutation } from '~/components/Blocks/Nodes/Audio/BlockMutationRecordPlayback.generated';
import type { CreateAssignmentMutation } from '~/components/Blocks/CreateAssignment.generated';
import type { DeleteAssignmentMutation } from '~/components/Blocks/DeleteAssignment.generated';
import type { BlockDataFragment } from '~/components/Blocks/graphql/BlockDataFragment.generated';
import { BlockDataFragmentDoc } from '~/components/Blocks/graphql/BlockDataFragment.generated';
import { updateBlockPositionOnConnection } from '~/components/Blocks/lib/updateBlockPositionOnConnection';
import { updateBlockPositions } from '~/components/Blocks/lib/updateBlockPositions';
import { FileUploadAnswerDataDocument } from '~/components/Blocks/Nodes/FileUpload/AnswerQuery.generated';
import { TaskBlockBlockFragmentDoc } from '~/components/Blocks/Nodes/TaskBlock/BlockFragment.generated';
import type { UpdateTaskBlockBlockMutation } from '~/components/Blocks/Nodes/TaskBlock/BlockMutationUpdate.generated';
import type { TaskBlockBlockDataQuery } from '~/components/Blocks/Nodes/TaskBlock/BlockQuery.generated';
import { TaskBlockBlockDataDocument } from '~/components/Blocks/Nodes/TaskBlock/BlockQuery.generated';
import { CurrentUserDocument } from '~/components/CurrentUserQuery.generated';
import type { UpdateUsersFavouritesMutation } from '~/components/shared/Dashboard/UpdateUsersFavouritesMutation.generated';
import type { DeleteMediaObjectMutation } from '~/components/shared/DeleteMediaObjectMutation.generated';
import type { UploadMediaObjectMutation } from '~/components/shared/UploadMediaObjectMutation.generated';
import type { CreateEventMutation } from '~/components/teacher/InnerPage/Events/CreateEventMutation.generated';
import type { DeleteExamEventMutation } from '~/components/teacher/InnerPage/Events/DeleteExamEventMutation.generated';
import type { ExamAuthorExtendAutoArchiveMutation } from '~/components/teacher/InnerPage/General/graphql/ExamAuthorQuery.generated';
import { ExamAuthorDocument } from '~/components/teacher/InnerPage/General/graphql/ExamAuthorQuery.generated';
import type { AddMachineMutation } from '~/components/teacher/InnerPage/VirtualEnvironment/AddMachine/AddMachineMutation.generated';
import type { CreateVmHddTemplateMutation } from '~/components/teacher/InnerPage/VirtualEnvironment/CreateVmHddTemplateMutation.generated';
import type { DeleteVmHddTemplateMutation } from '~/components/teacher/InnerPage/VirtualEnvironment/DeleteVmHddTemplateMutation.generated';
import { MachineTemplateDocument } from '~/components/teacher/InnerPage/VirtualEnvironment/VmModal/MachineTemplateQuery.generated';
import type { CopyExamMutation } from '~/components/teacher/Selection/Exams/CopyExam/CopyExamMutation.generated';
import type { CreateExamMutation } from '~/components/teacher/Selection/Exams/CreateExam/CreateExamMutation.generated';
import type { ExamCreateVariantMutation } from '~/components/teacher/Selection/Exams/CreateExam/ExamCreateVariantMutation.generated';
import type { ArchiveExamMutation } from '~/components/teacher/Selection/Exams/DeleteExam/ArchiveExamMutation.generated';
import type { DeleteExamMutation } from '~/components/teacher/Selection/Exams/DeleteExam/DeleteExamMutation.generated';
import schemaSdl from '~/lib/graphql/api/sml-api-schema.generated.graphql?raw';
import { getToken, smlUrqlAuthExchangeInit } from '~/lib/auth';
import { dedupFragmentsExchange } from '~/lib/dedupFragmentExchange';
import { getSafeExamBrowserHttpHeaders } from '~/lib/safeExamBrowser';
import scalarExchange from '~/lib/scalarExchange';
import { dateToString } from '~/lib/util/dateToString';
import { fromEntries } from '~/lib/util/fromEntries';
import { stringToDate } from '~/lib/util/stringToDate';
import type { ExamDetailsAssignmentPageQuery } from '~/routes/exams/assignments/assignment/graphql/ExamDetailsAssignmentPageQuery.generated';
import { ExamDetailsAssignmentPageDocument } from '~/routes/exams/assignments/assignment/graphql/ExamDetailsAssignmentPageQuery.generated';
import type { UpdateExamDetailsAssignmentPageMutation } from '~/routes/exams/assignments/assignment/graphql/UpdateExamDetailsAssignmentPageMutation.generated';
import { ExamDetailsAssignmentsPageDocument } from '~/routes/exams/assignments/graphql/ExamDetailsAssignmentsPageQuery.generated';
import { store } from '~/store/store';

const graphqlApiUrl = `${env.VITE_API_BASE_URL}${env.VITE_GRAPHQL_API_ENDPOINT}`;

// Built once from the SDL text export (~140 KB) and shared by the scalar
// exchange and graphcache, instead of bundling two generated introspection
// JSON documents (~2 MB combined) into the startup chunk.
const apiSchema = buildSchema(schemaSdl, { assumeValidSDL: true });

/**
 * Arguments the block lists are queried with. They are part of the cache key,
 * so a list can only be addressed with the very same arguments the queries in
 * `ExamDetailsAssignmentPage` and `TaskBlockBlock` use.
 */
const BLOCK_LIST_ARGS = { first: 1000 };

const handleBlockUpdate = (
    cache: GraphCache,
    block: { taskBlock?: { id?: string } } | { assignment?: { id?: string } },
    update: (blockConnection: BlockCursorConnection) => BlockCursorConnection,
) => {
    if (!block) {
        return;
    }

    if ('taskBlock' in block && block?.taskBlock?.id) {
        const id = block.taskBlock.id;
        const variables = { id };
        const cached = cache.readQuery({
            query: TaskBlockBlockDataDocument,
            variables,
        }) as TaskBlockBlockDataQuery | null;

        if (cached?.taskBlock) {
            cache.updateQuery(
                { query: TaskBlockBlockDataDocument, variables },
                (data: TaskBlockBlockDataQuery) => {
                    // TODO: Fix Typing here somehow
                    // @ts-ignore
                    data.taskBlock.blocks = update(data.taskBlock.blocks);
                    return data;
                },
            );
        } else {
            // The list is not (fully) cached yet — patching it would reach
            // into a query the cache cannot answer, which throws inside the
            // exchange and leaves the mutation unresolved. Dropping the list
            // makes it fetch again, with the change already in it. Only the
            // list: dropping the task block itself would unmount the editor
            // around it, and the block that was just created would lose the
            // state that opens it for editing.
            cache.invalidate(
                { __typename: 'TaskBlock', id },
                'blocks',
                BLOCK_LIST_ARGS,
            );
        }
    }

    if ('assignment' in block && block.assignment?.id) {
        const id = block.assignment.id;
        const variables = { id };
        const cached = cache.readQuery({
            query: ExamDetailsAssignmentPageDocument,
            variables,
        }) as ExamDetailsAssignmentPageQuery | null;

        if (cached?.assignment) {
            cache.updateQuery(
                { query: ExamDetailsAssignmentPageDocument, variables },
                (data: ExamDetailsAssignmentPageQuery) => {
                    // TODO: Fix Typing here somehow
                    // @ts-ignore
                    data.assignment.blocks = update(data.assignment.blocks);
                    return data;
                },
            );
        } else {
            cache.invalidate(
                { __typename: 'Assignment', id },
                'blocks',
                BLOCK_LIST_ARGS,
            );
        }
    }
};

const getBlockMutations = () => {
    const blockTypes = [
        'RichTextBlock',
        'FilesBlock',
        'CodeBlock',
        'AudioBlock',
        'ScreenshotBlock',
        'FileUploadBlock',
        'FileTransferBlock',
        'CodeAreaBlock',
        'DrawingBlock',
        'TextInputBlock',
        'SingleChoiceBlock',
        'SequenceBlock',
        'MultipleChoiceBlock',
        'MatrixChoiceBlock',
        'RightWrongAssertionBlock',
        'RightWrongDontKnowBlock',
        'TaskBlock',
    ];

    const createMutations = fromEntries(
        blockTypes.map((blockType) => {
            const createHandler = (result, _args, cache) => {
                const block = result[`create${blockType}`]?.[
                    uncapitalize(blockType)
                ] as BlockDataFragment;

                handleBlockUpdate(cache, block, (blockConnection) => {
                    // Add the new block as an edge at the end of all edges.
                    // Later, sort. We put some fake cursor to it, it shouldn't matter as long as it is unique,
                    // as it will not be shared with the server and be replaced on the next paginated request.
                    const newEdge: BlockEdge = {
                        __typename: 'BlockEdge',
                        node: block as unknown as Block,
                        cursor: block.id,
                    };

                    const connectionWithMergedEdges = {
                        ...blockConnection,
                        edges: [...blockConnection.edges, newEdge],
                    };

                    // Sort and return the list with the newly added block.
                    return updateBlockPositionOnConnection(
                        { id: block.id, position: block.position },
                        connectionWithMergedEdges,
                    );
                });
            };

            return [`create${blockType}`, createHandler] as const;
        }),
    );

    const deleteMutations = fromEntries(
        blockTypes.map((blockType) => {
            const deleteHandler = (result, _args, cache) => {
                const block = result[`delete${blockType}`]?.[
                    uncapitalize(blockType)
                ] as BlockDataFragment;

                const cachedBlock = cache.readFragment(BlockDataFragmentDoc, {
                    __typename: block.__typename,
                    id: block.id,
                }) as BlockDataFragment | null;

                if (!cachedBlock) {
                    return;
                }

                handleBlockUpdate(cache, cachedBlock, (blockConnection) => {
                    const indexOfItem = blockConnection.edges?.findIndex(
                        (edge) => edge.node.id === block.id,
                    );

                    if (indexOfItem !== -1) {
                        const remainingEdges = [...blockConnection.edges];
                        remainingEdges.splice(indexOfItem, 1);

                        return updateBlockPositions({
                            ...blockConnection,
                            edges: remainingEdges,
                        });
                    }

                    return updateBlockPositions(blockConnection);
                });
            };

            return [`delete${blockType}`, deleteHandler] as const;
        }),
    );

    return { ...createMutations, ...deleteMutations };
};

const moveInSortedAssignments = <T extends AssignmentDataFragment>(
    connection: T[],
    item: T,
): T[] => {
    const rest = connection.filter((it) => it.id !== item.id);
    const parentList = rest.map((it) => it.parent?.id);
    const parentSet = new Set([...parentList, item.parent?.id]);

    let arr = [];
    parentSet.forEach((parentId) => {
        const list = rest.filter((it) => it.parent?.id === parentId);

        if (item.parent?.id === parentId) {
            list.splice(item.position, 0, {
                ...item,
                __typename: 'Assignment',
            });
        }

        const ordered = list.map((it, index) => ({ ...it, position: index }));
        arr = [...arr, ...ordered];
    });

    return arr;
};

const invalidateUnfilledBlocksInTaskBlocksQuery = (
    _result,
    _args,
    cache: GraphCache,
) => {
    const key = 'Query';
    cache
        .inspectFields(key)
        .filter(
            (field) => field.fieldName === 'unfilledBlocksInTaskBlockBlocks',
        )
        .forEach((field) => {
            cache.invalidate(key, field.fieldKey);
        });
};

export const clientOptions: ClientOptions = {
    url: graphqlApiUrl,
    suspense: true,
    requestPolicy: 'cache-and-network',
    // No benefit from optimization via GET requests due to all queries running authenticated.
    preferGetMethod: false,
    exchanges: [
        devtoolsExchange,
        scalarExchange({
            schema: apiSchema,
            scalars: {
                DateTime: {
                    serialize: dateToString,
                    deserialize: stringToDate,
                },
            },
        }),
        cacheExchange({
            schema: minifyIntrospectionQuery(
                introspectionFromSchema(apiSchema, { descriptions: false }),
            ),
            keys: {
                ChoiceItem: (data: Data & ChoicesResolverResult) => {
                    // Use custom cache key generated in the resolver section, as
                    // choices do not have unqiue ids and therefore, won't be cached
                    // correctly otherwise.
                    return data.__customCacheId;
                },
            },
            resolvers: {
                Query: {
                    allBlocksByExamBlocks: relayPagination(),
                    blocks: relayPagination(),
                    courses: relayPagination(),
                    exams: relayPagination(),
                    examEvents: relayPagination(),
                    examPassLogs: relayPagination(),
                    unfilledBlocksInTaskBlockBlocks: relayPagination(),

                    // Cache resolvers for block queries - enables cache-first resolution
                    // by linking single-entity queries to entities already in cache
                    taskBlock: (_parent, args) => {
                        return { __typename: 'TaskBlock', id: args.id };
                    },
                    richTextBlock: (_parent, args) => {
                        return { __typename: 'RichTextBlock', id: args.id };
                    },
                    codeBlock: (_parent, args) => {
                        return { __typename: 'CodeBlock', id: args.id };
                    },
                    audioBlock: (_parent, args, cache) => {
                        const block = {
                            __typename: 'AudioBlock',
                            id: args.id,
                        } as Data;

                        // Pages cache the block without its playback, and
                        // serving that would show an untouched play limit until
                        // the real count lands. A cached `null` is an answer, a
                        // missing one is not.
                        return undefined ===
                            cache.resolve(block, 'audioBlockPlayback')
                            ? undefined
                            : block;
                    },
                    filesBlock: (_parent, args) => {
                        return { __typename: 'FilesBlock', id: args.id };
                    },
                    codeAreaBlock: (_parent, args) => {
                        return { __typename: 'CodeAreaBlock', id: args.id };
                    },
                    drawingBlock: (_parent, args) => {
                        return { __typename: 'DrawingBlock', id: args.id };
                    },
                    fileTransferBlock: (_parent, args) => {
                        return { __typename: 'FileTransferBlock', id: args.id };
                    },
                    fileUploadBlock: (_parent, args) => {
                        return { __typename: 'FileUploadBlock', id: args.id };
                    },
                    matrixChoiceBlock: (_parent, args) => {
                        return { __typename: 'MatrixChoiceBlock', id: args.id };
                    },
                    multipleChoiceBlock: (_parent, args) => {
                        return {
                            __typename: 'MultipleChoiceBlock',
                            id: args.id,
                        };
                    },
                    rightWrongAssertionBlock: (_parent, args) => {
                        return {
                            __typename: 'RightWrongAssertionBlock',
                            id: args.id,
                        };
                    },
                    rightWrongDontKnowBlock: (_parent, args) => {
                        return {
                            __typename: 'RightWrongDontKnowBlock',
                            id: args.id,
                        };
                    },
                    screenshotBlock: (_parent, args) => {
                        return { __typename: 'ScreenshotBlock', id: args.id };
                    },
                    sequenceBlock: (_parent, args) => {
                        return { __typename: 'SequenceBlock', id: args.id };
                    },
                    singleChoiceBlock: (_parent, args) => {
                        return { __typename: 'SingleChoiceBlock', id: args.id };
                    },
                    textInputBlock: (_parent, args) => {
                        return { __typename: 'TextInputBlock', id: args.id };
                    },
                },
                Assignment: {
                    blocks: relayPagination(),
                },
                TaskBlock: {
                    blocks: relayPagination(),
                },
                RightWrongDontKnowAnswer: {
                    choices: (parent: RightWrongDontKnowAnswer) =>
                        // The API returns an empty array for when a user has no answers given yet. Put if the user has given answers prior, the API returns an object.
                        // We resolve this inconsistency by always casting it to an empty object. This is what the UI would expect.
                        !parent ||
                        (parent?.choices &&
                            Array.isArray(parent.choices) &&
                            parent.choices.length === 0)
                            ? {}
                            : parent.choices,
                },
                MatrixChoiceAnswer: {
                    choices: (parent: MatrixChoiceAnswer) =>
                        // The API returns an empty array for when a user has no answers given yet. But if the user has given answers prior, the API returns an object.
                        // We resolve this inconsistency by always casting it to an empty object. This is what the UI would expect.
                        !parent ||
                        (parent?.choices &&
                            Array.isArray(parent.choices) &&
                            parent.choices.length === 0)
                            ? {}
                            : parent.choices,
                },
                SingleChoiceBlock: {
                    choices: choicesResolver,
                },
                MultipleChoiceBlock: {
                    choices: choicesResolver,
                },
            },
            optimistic: {
                updateBlock: (variables: MutationUpdateBlockArgs) => {
                    if (variables.input.position !== undefined) {
                        return {
                            __typename: 'updateBlockPayload',
                            block: {
                                id: variables.input.id,
                                position: variables.input.position,
                                taskBlock: {
                                    id: variables.input.taskBlock,
                                },
                                assignment: {
                                    id: variables.input.assignment,
                                },
                            },
                        };
                    }

                    if (variables.input.countsTowardsProgress !== undefined) {
                        return {
                            __typename: 'updateBlockPayload',
                            block: {
                                id: variables.input.id,
                                countsTowardsProgress:
                                    variables.input.countsTowardsProgress,
                            },
                        };
                    }
                },
                updateTaskBlock: (
                    variables: MutationUpdateTaskBlockArgs,
                    cache,
                ) => {
                    const taskBlock = cache.readFragment(
                        TaskBlockBlockFragmentDoc,
                        { __typename: 'TaskBlock', id: variables.input.id },
                    );

                    return {
                        __typename: 'updateTaskBlockPayload',
                        taskBlock: {
                            ...taskBlock,
                            title: variables.input.title ?? taskBlock.title,
                        },
                    };
                },
                updateAssignment: (
                    variables: MutationUpdateAssignmentArgs,
                    cache,
                ) => {
                    const assignment = cache.readFragment(
                        AssignmentDataFragmentDoc,
                        { __typename: 'Assignment', id: variables.input.id },
                    );

                    if (!assignment) {
                        return null;
                    }

                    return {
                        __typename: 'updateAssignmentPayload',
                        assignment: {
                            ...assignment,
                            title: variables.input.title ?? assignment.title,
                            position:
                                variables.input.position ?? assignment.position,
                            parent: variables.input.parent
                                ? {
                                      id: variables.input.parent,
                                      __typename: 'Assignment',
                                  }
                                : null,
                        },
                    };
                },
                updateEvaluationPoint: (
                    variables: MutationUpdateEvaluationPointArgs,
                ) => {
                    // Was added to ensure a smooth UX in the correction settings interface
                    // when switching between simple evaluation point setting and the
                    // evaluation matrix. Mainly ensures direct reset of description on
                    // removing the last item from the matrix, which needs to reset the
                    // description to allow to differentiate between simple points input
                    // and the matrix.
                    return {
                        __typename: 'updateEvaluationPointPayload',
                        evaluationPoint: {
                            __typename: 'EvaluationPoint',
                            id: variables.input.id,
                            title: variables.input.title,
                            description: variables.input.description,
                            points: variables.input.points,
                        },
                        clientMutationId: null,
                    };
                },
            },
            updates: {
                Mutation: {
                    recordAudioBlockPlayback: (
                        result: RecordAudioBlockPlaybackMutation,
                        args: MutationRecordAudioBlockPlaybackArgs,
                        cache,
                    ) => {
                        const playback =
                            result.recordAudioBlockPlayback?.audioBlockPlayback;

                        if (!playback) {
                            return;
                        }

                        // A block without any playback yet carries a null link,
                        // so the normalized payload alone would never reach it.
                        // Linking it here rather than invalidating the block
                        // keeps the counter updating without a refetch — the
                        // block would otherwise vanish while it reloads.
                        cache.link(
                            { __typename: 'AudioBlock', id: args.input.id },
                            'audioBlockPlayback',
                            {
                                __typename: 'AudioBlockPlayback',
                                id: playback.id,
                            },
                        );
                    },
                    archiveExam: (
                        result: ArchiveExamMutation,
                        args: MutationArchiveExamArgs,
                        cache,
                    ) => {
                        if (!result.archiveExam?.exam?.archivedAt) {
                            return;
                        }

                        // Ensure exam is pulled completetly to make flags like
                        // `canCreateVariant`` properly update all over the UI
                        cache.invalidate({
                            __typename: 'Exam',
                            id: result.archiveExam.exam.id,
                        });

                        // Forcefully invalidate all exam lists after archiving
                        // to ensure the dashboards requests their lists
                        // again with correct pagination and the correct states.
                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'exams')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });
                    },
                    createCorrectionApproval: createCorrectionApprovalUpdate,
                    deleteCorrectionApproval: deleteCorrectionApprovalUpdate,
                    createExam: (
                        result: CreateExamMutation,
                        args: MutationCreateExamArgs,
                        cache,
                    ) => {
                        if (!result.createExam) {
                            return;
                        }

                        // Invalidate all exam queries to ensure they are requests again from the server
                        // and therefore correctly apply new pagination values
                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'exams')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });
                    },
                    updateExam: (
                        result,
                        args: MutationUpdateExamArgs,
                        cache,
                    ) => {
                        if (!args.input.assignmentsEnabled) {
                            cache.invalidate(
                                {
                                    __typename: 'Exam',
                                    id: args.input.id,
                                },
                                'assignments',
                            );
                        }
                    },
                    createVariantExam: (
                        result: ExamCreateVariantMutation,
                        args: MutationCreateVariantExamArgs,
                        cache,
                    ) => {
                        if (!result.createVariantExam) {
                            return;
                        }

                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'exams')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });
                    },
                    createCopyExam: (
                        result: CopyExamMutation,
                        _args,
                        cache,
                    ) => {
                        if (!result.createCopyExam) {
                            return;
                        }

                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'exams')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });
                    },
                    deleteExam: (
                        result: DeleteExamMutation,
                        args: MutationDeleteExamArgs,
                        cache,
                    ) => {
                        if (!result.deleteExam) {
                            return;
                        }

                        cache.invalidate({
                            __typename: 'Exam',
                            id: args.input.id,
                        });

                        // Forcefully invalidate all exam lists after deletion
                        // to ensure the dashboards requests their lists
                        // again with correct pagination and the correct states.
                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'exams')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });
                    },
                    createExamEvent: (
                        result: CreateEventMutation,
                        args: MutationCreateExamEventArgs,
                        cache,
                    ) => {
                        if (!result.createExamEvent) {
                            return;
                        }

                        // Invalidate all examEvents queries to ensure they are requests again from the server
                        // and therefore correctly apply new pagination values
                        const key = 'Query';
                        cache
                            .inspectFields(key)
                            .filter((field) => field.fieldName === 'examEvents')
                            .forEach((field) => {
                                cache.invalidate(key, field.fieldKey);
                            });

                        // Invalidate the `events` field on the exam
                        // Both invalidations are necessary because urql's cache
                        // makes a distinction between queries with and without filters.
                        cache.invalidate(
                            {
                                __typename: 'Exam',
                                id: args.input.examVariant,
                            },
                            'events',
                        );

                        cache.invalidate(
                            {
                                __typename: 'Exam',
                                id: args.input.examVariant,
                            },
                            'events',
                            { exists: [{ archivedAt: false }] },
                        );
                    },
                    deleteExamEvent: (
                        result: DeleteExamEventMutation,
                        args: MutationDeleteExamEventArgs,
                        cache,
                    ) => {
                        if (!result.deleteExamEvent) {
                            return;
                        }

                        // Invalidate all examEvents queries to ensure they are requested
                        // again from the server and therefore correctly apply new
                        // pagination values
                        cache
                            .inspectFields('Query')
                            .filter((field) => field.fieldName === 'examEvents')
                            .forEach((field) => {
                                cache.invalidate('Query', field.fieldKey);
                            });

                        // Get the related examVariants of this exam events to invalidate
                        // their data first. cache.resolve returns a cache key string
                        // like "Exam:/api/exams/8", so we need to extract the ID part.
                        const examVariantCacheID = cache.resolve(
                            {
                                __typename: 'ExamEvent',
                                id: args.input.id,
                            },
                            'examVariant',
                        );

                        // Extract the ID from the cache key (e.g., "Exam:/api/exams/8" -> "/api/exams/8")
                        const examVariantId =
                            typeof examVariantCacheID === 'string'
                                ? examVariantCacheID.split(':')[1]
                                : null;

                        if (examVariantId) {
                            // Invalidate the `events` field on the exam
                            // Both invalidations are necessary because urql's cache
                            // makes a distinction between queries with and without filters.
                            cache.invalidate(
                                {
                                    __typename: 'Exam',
                                    id: examVariantId,
                                },
                                'events',
                            );

                            cache.invalidate(
                                {
                                    __typename: 'Exam',
                                    id: examVariantId,
                                },
                                'events',
                                { exists: [{ archivedAt: false }] },
                            );
                        }

                        // Invalidate any queries that directly reference this ExamEvent
                        const fieldsToInvalidate = cache
                            .inspectFields('Query')
                            .filter(
                                (field) =>
                                    field.fieldName === 'examEvent' ||
                                    field.fieldName ===
                                        'detailResultExamEvent' ||
                                    field.fieldName ===
                                        'permittedCorrectingUsers',
                            )
                            .filter((field) => {
                                // Check if the query arguments reference the deleted event
                                const fieldArgs = field.arguments as any;
                                return (
                                    fieldArgs?.id === args.input.id ||
                                    fieldArgs?.examOrExamEvent === args.input.id
                                );
                            });

                        fieldsToInvalidate.forEach((field) => {
                            cache.invalidate('Query', field.fieldKey);
                        });

                        cache.invalidate({
                            __typename: 'ExamEvent',
                            id: args.input.id,
                        });
                    },
                    extendAutoArchiveExam: (
                        result: ExamAuthorExtendAutoArchiveMutation,
                        args: MutationExtendAutoArchiveExamArgs,
                        cache,
                    ) => {
                        if (!result.extendAutoArchiveExam) {
                            return;
                        }

                        const { autoArchiveWarned, autoArchiveDate } =
                            result.extendAutoArchiveExam.exam;

                        cache.updateQuery(
                            {
                                query: ExamAuthorDocument,
                                variables: { examId: args.input.id },
                            },
                            (data) => {
                                data.exam.autoArchiveDate = autoArchiveDate;
                                data.exam.autoArchiveWarned = autoArchiveWarned;

                                return data;
                            },
                        );
                    },
                    removeMachineFromEnvironmentTemplate: (
                        _result,
                        args: MutationRemoveMachineFromEnvironmentTemplateArgs,
                        cache,
                        info,
                    ) => {
                        const environmentTemplateId = args.input.id;
                        const machineTemplateId = args.input.machine;

                        cache.invalidate(
                            {
                                __typename: 'EnvironmentTemplate',
                                id: environmentTemplateId,
                            },
                            'machines',
                        );

                        cache.invalidate({
                            __typename: 'MachineTemplate',
                            id: machineTemplateId,
                        });

                        // Extra variable not part of the Query itself
                        const examId = info?.variables?.examId as string;
                        const machineId = info?.variables?.machineId as string;

                        if (examId && machineId) {
                            // Remove machine with given machineId from given exam with given examId, to ensure
                            // `machines` of that exam's primary instance is not featuring a `null` reference.
                            cache.updateQuery(
                                {
                                    query: ExamPrimaryInstanceMachinesDocument,
                                    variables: { examId },
                                },
                                (data) => {
                                    data.exam.environment.primaryInstance.machines =
                                        data.exam.environment.primaryInstance.machines?.filter(
                                            (machine) =>
                                                machine.id !== machineId,
                                        );

                                    return data;
                                },
                            );
                        }
                    },
                    addMachineToEnvironmentTemplate: (
                        result: AddMachineMutation,
                        args: MutationAddMachineToEnvironmentTemplateArgs,
                        cache,
                    ) => {
                        if (!result.addMachineToEnvironmentTemplate) {
                            return;
                        }

                        // Network graph
                        cache.invalidate(
                            {
                                __typename: 'Environment',
                                id: result?.addMachineToEnvironmentTemplate
                                    ?.environmentTemplate?.primaryInstance?.id,
                            },
                            'machines',
                        );

                        // Tab count
                        cache.invalidate(
                            {
                                __typename: 'EnvironmentTemplate',
                                id: args.input.id,
                            },
                            'machines',
                        );
                    },
                    deleteDiskTemplate: (
                        result: DeleteVmHddTemplateMutation,
                        args: MutationDeleteDiskTemplateArgs,
                        cache,
                    ) => {
                        cache.updateQuery(
                            {
                                query: MachineTemplateDocument,
                                variables: {
                                    id: result?.deleteDiskTemplate?.diskTemplate
                                        ?.machine?.id,
                                },
                            },
                            (data) => {
                                if (data?.machineTemplate?.disks) {
                                    data.machineTemplate.disks =
                                        data?.machineTemplate?.disks?.filter(
                                            (disk) => disk.id !== args.input.id,
                                        );
                                }

                                return data;
                            },
                        );
                    },
                    createDiskTemplate: (
                        result: CreateVmHddTemplateMutation,
                        args: MutationCreateDiskTemplateArgs,
                        cache,
                    ) => {
                        cache.updateQuery(
                            {
                                query: MachineTemplateDocument,
                                variables: { id: args.input.machine },
                            },
                            (data) => {
                                data?.machineTemplate?.disks?.push(
                                    result.createDiskTemplate.diskTemplate,
                                );

                                return data;
                            },
                        );
                    },
                    addUsersToExamEvent: (
                        _result,
                        args: MutationAddUsersToExamEventArgs,
                        cache,
                    ) => {
                        // TODO: don't invalidate, just update

                        cache.invalidate(
                            { __typename: 'ExamEvent', id: args.input.id },
                            'passes',
                        );
                    },
                    removeUsersFromExamEvent: (
                        _result,
                        args: MutationRemoveUsersFromExamEventArgs,
                        cache,
                    ) => {
                        // TODO: don't invalidate, just update
                        cache.invalidate(
                            { __typename: 'ExamEvent', id: args.input.id },
                            'passes',
                        );
                    },
                    beginExamPass: (
                        result,
                        args: MutationBeginExamPassArgs,
                        cache,
                    ) => {
                        cache.invalidate({
                            __typename: 'ExamPass',
                            id: args.input.id,
                        });
                    },
                    finishExamPass: finishExamPassUpdate,
                    ...getBlockMutations(),
                    uploadMediaObject: (
                        result: UploadMediaObjectMutation,
                        args: MutationUploadMediaObjectArgs,
                        cache,
                    ) => {
                        const fileUploadAnswer = args.input.fileUploadAnswer;
                        if (fileUploadAnswer) {
                            invalidateUnfilledBlocksInTaskBlocksQuery(
                                result,
                                args,
                                cache,
                            );
                            cache.updateQuery(
                                {
                                    query: FileUploadAnswerDataDocument,
                                    variables: { id: fileUploadAnswer },
                                },
                                (data) => {
                                    // A failed upload resolves with a null
                                    // payload; pushing then throws inside the
                                    // graphcache pipeline and the mutation
                                    // never settles (endless loader).
                                    const mediaObject =
                                        result.uploadMediaObject?.mediaObject;
                                    if (mediaObject && data?.fileUploadAnswer) {
                                        data.fileUploadAnswer.files.push(
                                            mediaObject,
                                        );
                                    }
                                    return data;
                                },
                            );
                        }
                        const fileTransferAnswerCorrection =
                            args.input.fileTransferAnswerCorrection;
                        if (
                            fileTransferAnswerCorrection &&
                            // A failed upload resolves with a null payload, in
                            // which case there is nothing to add to the list.
                            result.uploadMediaObject?.mediaObject
                        ) {
                            // Patching the list through the display's own query
                            // only works as long as that query runs, and it is
                            // paused once the answer is handed over as a prop.
                            // Invalidating the field of the answer itself lets
                            // whichever query holds it load the list again.
                            cache.invalidate(
                                {
                                    __typename: 'FileTransferAnswer',
                                    id: fileTransferAnswerCorrection,
                                },
                                'correctionFiles',
                            );
                        }
                    },
                    deleteMediaObject: (
                        result: DeleteMediaObjectMutation,
                        args: MutationDeleteMediaObjectArgs,
                        cache,
                    ) => {
                        cache.invalidate(result.deleteMediaObject.mediaObject);
                    },
                    updateBlock: (
                        result: ReorderBlockMutation,
                        args: MutationUpdateBlockArgs,
                        cache,
                        info,
                    ) => {
                        // Is passed as true if this is an update concerning the position of the block.
                        // This is an extra prop passed to the useMutation hook and not part of the mutation query itself.
                        const positionUpdate = info.variables
                            ?.positionUpdate as boolean;

                        if (!result?.updateBlock?.block?.id) {
                            // Try invalidate the whole block first, if this doesn't work,
                            // try to invalidate the task block the updated block is nested
                            // to for a chance to load the data for just this task block
                            // again. In any other case, ignore cache updates.
                            if (result?.updateBlock?.block?.__typename) {
                                cache.invalidate({
                                    __typename:
                                        result.updateBlock.block.__typename,
                                    id: args.input.id,
                                });
                            } else if (args.input.taskBlock) {
                                cache.invalidate({
                                    __typename: 'TaskBlock',
                                    id: args.input.id,
                                });
                            } else {
                                return;
                            }
                        }

                        if (positionUpdate) {
                            // Update `position` and reorder the blocks on the fly, after the optimistic
                            // update and after the network update.
                            handleBlockUpdate(
                                cache,
                                {
                                    assignment: {
                                        id: args.input.assignment,
                                    },
                                    taskBlock: {
                                        id: args.input.taskBlock,
                                    },
                                },
                                (blockConnection) =>
                                    updateBlockPositionOnConnection(
                                        {
                                            id: result.updateBlock.block.id,
                                            position:
                                                result.updateBlock.block
                                                    .position,
                                        },
                                        blockConnection,
                                    ),
                            );
                        }

                        return result;
                    },
                    createAssignment: (
                        result: CreateAssignmentMutation,
                        _args,
                        cache,
                    ) => {
                        if (!result.createAssignment) {
                            return null;
                        }

                        const assignment = result.createAssignment.assignment;

                        cache.updateQuery(
                            {
                                query: ExamDetailsAssignmentsPageDocument,
                                variables: { exam: assignment.exam.id },
                            },
                            (data) => {
                                data.exam.assignments.push(assignment);

                                return data;
                            },
                        );
                    },
                    copyPasteAssignment: (
                        result: CopyPasteAssignmentMutation,
                        _args: MutationCopyPasteAssignmentArgs,
                        cache,
                    ) => {
                        if (!result.copyPasteAssignment) {
                            return null;
                        }

                        const newAssignment =
                            result.copyPasteAssignment.assignment;

                        cache.invalidate(
                            {
                                __typename: 'Exam',
                                id: newAssignment.exam.id,
                            },
                            'assignments',
                        );
                    },
                    deleteAssignment: (
                        result: DeleteAssignmentMutation,
                        args: MutationDeleteAssignmentArgs,
                        cache,
                    ) => {
                        if (!result.deleteAssignment) {
                            return;
                        }

                        cache.invalidate({
                            __typename: 'Assignment',
                            id: args.input.id,
                        });
                    },
                    updateAssignment: (
                        result: UpdateExamDetailsAssignmentPageMutation,
                        args: MutationUpdateAssignmentArgs,
                        cache,
                    ) => {
                        if (!result?.updateAssignment?.assignment) {
                            if (!args?.input?.id) {
                                return;
                            }

                            cache.invalidate({
                                __typename: 'Assignment',
                                id: args.input.id,
                            });

                            return;
                        }

                        const assignment = result.updateAssignment.assignment;

                        if (typeof args.input?.position === 'number') {
                            cache.invalidate(
                                {
                                    __typename: 'Assignment',
                                    id: assignment.id,
                                },
                                'position',
                            );
                        }

                        if (typeof args.input?.parent === 'string') {
                            cache.invalidate(
                                {
                                    __typename: 'Assignment',
                                    id: assignment.id,
                                },
                                'parent',
                            );
                        }

                        if (typeof args.input?.title === 'string') {
                            const title = assignment?.title;

                            cache.writeFragment(
                                AssignmentUpdateCacheFragmentDoc,
                                {
                                    __typename: 'Assignment',
                                    id: args.input.id,
                                    title,
                                    position: undefined,
                                    parent: undefined,
                                },
                            );
                        }

                        if (
                            args.input?.position !== undefined ||
                            args.input?.parent !== undefined
                        ) {
                            cache.invalidate(
                                {
                                    __typename: 'Exam',
                                    id: assignment.exam.id,
                                },
                                'assignments',
                            );
                        }
                    },
                    markAsSolutionForTextInputBlock: (
                        _result,
                        _args,
                        cache,
                    ) => {
                        cache.invalidate('Correction');
                    },
                    updateTaskBlock: (
                        result: UpdateTaskBlockBlockMutation,
                        args: MutationUpdateTaskBlockArgs,
                        cache,
                    ) => {
                        if (!result?.updateTaskBlock?.taskBlock) {
                            // On a failed update (e.g. rejected title) urql has
                            // already rolled back the optimistic write, so the
                            // cached task block is correct. Invalidating here would
                            // force a needless refetch that, under suspense, throws
                            // into the route error boundary and hides the assignment.
                            return;
                        }

                        const taskBlock = result.updateTaskBlock.taskBlock;

                        if (args.input.position !== undefined) {
                            handleBlockUpdate(
                                cache,
                                taskBlock,
                                (blockConnection) => {
                                    const updatedBlockConnection =
                                        updateBlockPositionOnConnection(
                                            taskBlock,
                                            blockConnection,
                                        );

                                    return updatedBlockConnection;
                                },
                            );
                        } else {
                            cache.updateQuery(
                                {
                                    query: TaskBlockBlockDataDocument,
                                    variables: { id: taskBlock.id },
                                },
                                (data) => {
                                    data.taskBlock = {
                                        ...data.taskBlock,
                                        ...taskBlock,
                                    };

                                    return data;
                                },
                            );
                        }
                    },
                    updateSingleChoiceAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateTextInputAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateCodeAreaAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateDrawingAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateMatrixChoiceAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateMultipleChoiceAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateRightWrongAssertionAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateRightWrongDontKnowAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateSequenceAnswer:
                        invalidateUnfilledBlocksInTaskBlocksQuery,
                    updateUser: (
                        result: UpdateUsersFavouritesMutation,
                        args: MutationUpdateUserArgs,
                        cache,
                    ) => {
                        const updatedUser = result.updateUser.user;

                        cache.updateQuery(
                            {
                                query: CurrentUserDocument,
                            },
                            (data) => {
                                if (!data) {
                                    return;
                                }

                                data.currentUser = {
                                    ...data.currentUser,
                                    ...updatedUser,
                                };

                                return data;
                            },
                        );
                    },
                    updateCorrection: (_result, _args, cache) => {
                        // Invalidate the correctedAnswersResultExamEvent query to refresh the result tab
                        // after point corrections are made. This ensures the displayed total points
                        // reflect the latest corrections from the correction tab.
                        cache
                            .inspectFields('Query')
                            .filter(
                                (field) =>
                                    field.fieldName ===
                                    'correctedAnswersResultExamEvent',
                            )
                            .forEach((field) =>
                                cache.invalidate('Query', field.fieldKey),
                            );
                    },
                    createEvaluationPoint: (
                        result: Pick<
                            {
                                createEvaluationPoint: CreateEvaluationPointPayload;
                            },
                            'createEvaluationPoint'
                        >,
                        args: MutationCreateEvaluationPointArgs,
                        cache,
                        info,
                    ) => {
                        if (!result.createEvaluationPoint?.evaluationPoint) {
                            return;
                        }

                        const evaluationPoint =
                            result.createEvaluationPoint.evaluationPoint;
                        const blockId = args.input.blockId;

                        // _blockTypenameis not part of the mutation query input itself
                        // but an additionally passed argument which, when given, we use
                        // to optimize cache without server roundtrips.
                        const blockTypename = info.variables
                            ?._blockTypename as string;

                        if (!blockTypename) {
                            // If we don't have the typename, invalidate the evaluation point
                            cache.invalidate({
                                __typename: 'EvaluationPoint',
                                id: evaluationPoint.id,
                            });
                            return;
                        }

                        // Update the block's evaluationPoints array in cache
                        const blockKey = cache.keyOfEntity({
                            __typename: blockTypename,
                            id: blockId,
                        });

                        if (blockKey) {
                            const currentPoints = cache.resolve(
                                blockKey,
                                'evaluationPoints',
                            );

                            if (Array.isArray(currentPoints)) {
                                cache.link(blockKey, 'evaluationPoints', [
                                    ...currentPoints,
                                    evaluationPoint,
                                ]);
                            }
                        } else {
                            cache.invalidate({
                                __typename: 'EvaluationPoint',
                                id: evaluationPoint.id,
                            });
                        }
                    },
                    deleteEvaluationPoint: (
                        result: Pick<
                            {
                                deleteEvaluationPoint: DeleteEvaluationPointPayload;
                            },
                            'deleteEvaluationPoint'
                        >,
                        args: MutationDeleteEvaluationPointArgs,
                        cache,
                    ) => {
                        if (!result.deleteEvaluationPoint?.evaluationPoint) {
                            return;
                        }

                        // Invalidate the deleted evaluation point entity
                        // This will cause queries using this entity to be refetched
                        cache.invalidate({
                            __typename: 'EvaluationPoint',
                            id: args.input.id,
                        });
                    },
                },
            },
        }),
        dedupFragmentsExchange,
        authExchange(smlUrqlAuthExchangeInit),
        fetchExchange,
    ],
    fetchOptions: () => ({
        credentials: 'include',
        headers: {
            Authorization: `Bearer ${getToken()}`,
            'X-Switch-User':
                store?.getState()?.userImpersonation?.impersonatedUser || '',
            ...getSafeExamBrowserHttpHeaders(),
        },
    }),
};

/**
 * Shared module-scope urql client. Used both by the React `<Provider>` (in
 * `root.tsx`) and by route-module loaders, so loader prefetches and hook-driven
 * `useQuery` calls share the same normalized graphcache.
 */
export const urqlClient = createClient(clientOptions);
